/**
 * Policies list — filter model (OR-1664, design OR-1537).
 *
 * Pure data + conversions; the drawer that edits it is `filters-drawer.tsx`.
 * Every clause maps to a real `GET /v2/policies` query param:
 *
 *   Priority        → `priority` (repeated)
 *   Status          → `status` (repeated)
 *   Inception Date  → `min_effective_date` / `max_effective_date`
 *   Expiration Date → `min_expiration_date` / `max_expiration_date`
 *   Brokerage       → `brokerage_id`
 *
 * Expiration has two modes the design puts side by side: a quick preset
 * ("Next 30 / 90 / 180 days", resolved from today when the request is
 * built, so a saved layout keeps meaning "the next 30 days" tomorrow) and
 * a custom range. They are mutually exclusive — picking one clears the
 * other — because both write the same two params.
 */
import { type CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import type { ListPoliciesParams, PolicyPriority, PolicyTermStatus } from "../../api";

/** A picked option that carries its id (sent to the API) + label (chips). */
export interface FilterOption {
    id: string;
    label: string;
}

export const EXPIRATION_PRESETS = ["30", "90", "180"] as const;
export type ExpirationPreset = (typeof EXPIRATION_PRESETS)[number];

export interface PolicyFilters {
    priorities: PolicyPriority[];
    statuses: PolicyTermStatus[];
    /** ISO `YYYY-MM-DD`, "" = unset. */
    inceptionStart: string;
    inceptionEnd: string;
    /** Days from today, or "" when a custom range (or nothing) is set. */
    expirationPreset: ExpirationPreset | "";
    expirationStart: string;
    expirationEnd: string;
    brokerage: FilterOption | null;
}

export const EMPTY_FILTERS: PolicyFilters = {
    priorities: [],
    statuses: [],
    inceptionStart: "",
    inceptionEnd: "",
    expirationPreset: "",
    expirationStart: "",
    expirationEnd: "",
    brokerage: null,
};

export const PRIORITY_LABELS: Record<PolicyPriority, string> = {
    high: "High",
    medium: "Medium",
    low: "Low",
};

/** On-screen names for the term statuses, in the order the design lists
 *  them in the Filters drawer. */
export const STATUS_LABELS: Record<PolicyTermStatus, string> = {
    in_force: "In force",
    pending_cancellation: "Pending cancellation",
    bound: "Bound",
    pending_inception: "Pending Inception",
    run_off: "Run-off",
    cancelled: "Cancelled",
    expired: "Expired",
    void: "Void",
};

export const PRESET_LABELS: Record<ExpirationPreset, string> = {
    "30": "Next 30 days",
    "90": "Next 90 days",
    "180": "Next 180 days",
};

/** The inclusive `[today, today + days]` window a preset stands for. */
export function presetRange(preset: ExpirationPreset, from: CalendarDate = today(getLocalTimeZone())): { start: string; end: string } {
    return { start: from.toString(), end: from.add({ days: Number(preset) }).toString() };
}

/** Committed filters → real backend query params. `from` is injectable
 *  so the preset window is testable; it defaults to today. */
export function filtersToParams(f: PolicyFilters, from?: CalendarDate): Partial<ListPoliciesParams> {
    const p: Partial<ListPoliciesParams> = {};
    if (f.priorities.length) p.priority = f.priorities;
    if (f.statuses.length) p.status = f.statuses;
    if (f.brokerage) p.brokerageId = f.brokerage.id;
    if (f.inceptionStart) p.minEffectiveDate = f.inceptionStart;
    if (f.inceptionEnd) p.maxEffectiveDate = f.inceptionEnd;
    if (f.expirationPreset) {
        const range = presetRange(f.expirationPreset, from);
        p.minExpirationDate = range.start;
        p.maxExpirationDate = range.end;
    } else {
        if (f.expirationStart) p.minExpirationDate = f.expirationStart;
        if (f.expirationEnd) p.maxExpirationDate = f.expirationEnd;
    }
    return p;
}

/** Badge count on the Filters button: one per checkbox, one per clause. */
export function countActiveFilters(f: PolicyFilters): number {
    let n = f.priorities.length + f.statuses.length;
    if (f.brokerage) n += 1;
    if (f.inceptionStart || f.inceptionEnd) n += 1;
    if (f.expirationPreset || f.expirationStart || f.expirationEnd) n += 1;
    return n;
}
